/**
 * useTelemetry Hook
 * 
 * React hook for vehicle telemetry data with:
 * - Real-time telemetry streaming via Socket.IO
 * - Historical telemetry queries
 * - Vehicle route visualization
 * - Telemetry statistics
 */

import { useState, useEffect, useCallback } from 'react';
import { useTelemetryContext } from '../context/TelemetryContext';
import api from '../lib/api';

export function useTelemetry() {
  const { liveTelemetry, connected, subscribeTo } = useTelemetryContext();

  /**
   * Get latest telemetry for a vehicle from database
   */
  const getLatest = useCallback(async (vehicleId) => {
    try {
      const data = await api.get(`/api/telemetry/vehicle/${vehicleId}/latest`);
      return data;
    } catch (error) {
      console.error('Failed to get latest telemetry:', error);
      return null;
    }
  }, []);

  /**
   * Get telemetry history for a vehicle
   */
  const getHistory = useCallback(async (vehicleId, options = {}) => {
    try {
      const { start, end, limit } = options;
      const params = new URLSearchParams();
      
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      if (limit) params.append('limit', limit);

      const data = await api.get(`/api/telemetry/vehicle/${vehicleId}/history?${params}`);
      return data;
    } catch (error) {
      console.error('Failed to get telemetry history:', error);
      return null;
    }
  }, []);

  /**
   * Get vehicle route for map visualization
   */
  const getRoute = useCallback(async (vehicleId, options = {}) => {
    try {
      const { start, end, maxPoints } = options;
      const params = new URLSearchParams();
      
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      if (maxPoints) params.append('maxPoints', maxPoints);

      const data = await api.get(`/api/telemetry/vehicle/${vehicleId}/route?${params}`);
      return data;
    } catch (error) {
      console.error('Failed to get vehicle route:', error);
      return null;
    }
  }, []);

  /**
   * Get telemetry statistics for a vehicle
   */
  const getStats = useCallback(async (vehicleId, options = {}) => {
    try {
      const { start, end } = options;
      const params = new URLSearchParams();
      
      if (start) params.append('start', start);
      if (end) params.append('end', end);

      const data = await api.get(`/api/telemetry/stats/${vehicleId}?${params}`);
      return data;
    } catch (error) {
      console.error('Failed to get telemetry stats:', error);
      return null;
    }
  }, []);

  /**
   * Get live telemetry for all vehicles
   */
  const getAllLive = useCallback(async () => {
    try {
      const data = await api.get('/api/telemetry/live/all');
      return data;
    } catch (error) {
      console.error('Failed to get all live telemetry:', error);
      return null;
    }
  }, []);

  return {
    // Live telemetry state
    liveTelemetry,
    connected,
    
    // Methods
    subscribeTo,
    getLatest,
    getHistory,
    getRoute,
    getStats,
    getAllLive
  };
}

/**
 * useVehicleLiveTelemetry Hook
 * 
 * Hook for subscribing to live telemetry for a specific vehicle
 * Automatically manages subscription lifecycle
 */
export function useVehicleLiveTelemetry(imei) {
  const [telemetry, setTelemetry] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const { subscribeTo, connected } = useTelemetry();

  useEffect(() => {
    if (!imei || !connected) return;

    const unsubscribe = subscribeTo(imei, (data) => {
      setTelemetry(data);
      setLastUpdate(new Date());
    });

    return unsubscribe;
  }, [imei, connected, subscribeTo]);

  return {
    telemetry,
    lastUpdate,
    connected
  };
}

/**
 * useVehicleTelemetryHistory Hook
 * 
 * Hook for loading and managing vehicle telemetry history
 */
export function useVehicleTelemetryHistory(vehicleId, options = {}) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getHistory } = useTelemetry();

  const loadHistory = useCallback(async () => {
    if (!vehicleId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getHistory(vehicleId, options);
      setHistory(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, getHistory, options]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return {
    history,
    loading,
    error,
    reload: loadHistory
  };
}

/**
 * useVehicleRoute Hook
 * 
 * Hook for loading vehicle route data for map visualization
 */
export function useVehicleRoute(vehicleId, options = {}) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getRoute } = useTelemetry();

  const loadRoute = useCallback(async () => {
    if (!vehicleId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getRoute(vehicleId, options);
      setRoute(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, getRoute, options]);

  useEffect(() => {
    loadRoute();
  }, [loadRoute]);

  return {
    route,
    loading,
    error,
    reload: loadRoute
  };
}

export default useTelemetry;

